import React, { useState, useEffect, useCallback } from 'react';
import SimpleProblemPreview from './SimpleProblemPreview';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const ExamProblemList = ({ 
  examId, 
  stampId, 
  accessToken, 
  onViewProblem,
  onProblemClick,
  isDesktop = false 
}) => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(null);

  // 문제 목록 및 미리보기 데이터 로드
  const loadExamProblems = useCallback(async () => {
    if (!examId || !stampId || !accessToken) {
      setError('필수 파라미터가 없습니다.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/api/exams/${stampId}/${examId}/problems?include_answer=true&include_lexical=true&include_result=true&include_solution=true`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`API 요청 실패: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data) {
        // problems_info는 배열 형태
        const problemsInfo = data.data.problems_info || [];
        
        const problemsArray = problemsInfo.map((info, index) => ({
          problem_id: info.problem_id,
          step: info.step || info.difficulty || info.level || null,
          address: info.address || info.problem_address || null,
          is_correct: info.result?.result ?? null, // result.result 필드 사용
          lexical_data: info.lexical || null, // lexical 데이터 포함
          answer: info.answer || null,
          solution: info.solution || null,
          ...info
        }));

        // problem_id로 정렬
        problemsArray.sort((a, b) => a.problem_id - b.problem_id);

        setProblems(problemsArray);
        console.log('문제 목록 로드 성공:', problemsArray.length, '개 문제');
        console.log('첫 번째 문제 데이터:', problemsArray[0]);
      } else {
        throw new Error('API 응답 데이터가 올바르지 않습니다.');
      }
    } catch (error) {
      console.error('문제 목록 로드 실패:', error);
      setError(error.message || '문제 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [examId, stampId, accessToken]);

  useEffect(() => {
    loadExamProblems();
  }, [loadExamProblems]);

  const handleHighlight = (index, problem) => {
    setHighlightedIndex(index);
  };

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-sm text-gray-600">문제 목록을 불러오는 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full text-center py-8">
        <p className="text-sm text-red-500 mb-2">오류가 발생했습니다</p>
        <p className="text-xs text-gray-500">{error}</p>
        <button 
          onClick={loadExamProblems}
          className="mt-3 px-4 py-2 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          다시 시도
        </button>
      </div>
    );
  }

  if (problems.length === 0) {
    return (
      <div className="w-full text-center py-8">
        <p className="text-sm text-gray-500">문제 정보가 없습니다.</p>
      </div>
    );
  }

  return (
    <SimpleProblemPreview
      problems={problems}
      onViewProblem={onViewProblem}
      onProblemClick={onProblemClick}
      highlightedIndex={highlightedIndex}
      onHighlight={handleHighlight}
      accessToken={accessToken}
      isDesktop={isDesktop}
    />
  );
};

export default ExamProblemList;
